export enum NotificationType {
	TICKET_CREATED = 'ticket_created',
	/**
	 * Daily approver digest fired by `RequestCenterDigestWorker` per
	 * approver with ≥ 1 pending approval. Carries the
	 * `pending-approvals-digest` MJML template.
	 */
	PENDING_APPROVALS_DIGEST = 'pending_approvals_digest',
	/**
	 * Per-approval-step Teams DM fired by `teams-approval-producer` when a
	 * `REQUEST_APPROVAL_PENDING` event lands — one per resolved approver.
	 * Distinct from `PENDING_APPROVALS_DIGEST` (the daily email batch).
	 */
	APPROVAL_PENDING = 'approval_pending',
	/**
	 * Teams group-chat announcement fired by `teams-lifecycle-producer` when
	 * a `team-member-onboarding` request is submitted.
	 */
	ONBOARDING_STARTED = 'onboarding_started',
	/**
	 * Teams group-chat announcement fired by `teams-lifecycle-producer` when
	 * a `team-member-offboarding` request is submitted.
	 */
	OFFBOARDING_STARTED = 'offboarding_started',
}

export enum NotificationChannel {
	EMAIL = 'email',
	TEAMS = 'teams',
}

export enum NotificationStatus {
	PENDING = 'pending',
	SENT = 'sent',
	FAILED = 'failed',
	/**
	 * The notification was received by the worker but the per-stage feature
	 * flag is disabled, so no send was attempted. The DB row exists for
	 * observability during rollout — drop the rows before flipping the flag
	 * if the volume becomes a concern.
	 */
	SKIPPED_DISABLED = 'skipped_disabled',
}

/**
 * Where a Teams notification is delivered. Distinct from chat-message
 * addressing in Bot Framework — `USER` means a 1:1 chat with a person
 * (resolved from email → Entra user ID via the local mirror); `GROUP_CHAT`
 * means an existing multi-person Teams chat addressed by chat ID.
 */
export enum NotificationRecipientKind {
	USER = 'user',
	GROUP_CHAT = 'group_chat',
}

/**
 * Compile-time-checked names for Teams notification body templates rendered
 * by the worker's `TeamsTemplateRenderer`. Mirrors `EmailTemplateName` so
 * producers reference enum members instead of string literals.
 */
export enum TeamsNotificationTemplate {
	PENDING_APPROVAL = 'pending-approval',
	NEW_ONBOARDING = 'new-onboarding',
	NEW_OFFBOARDING = 'new-offboarding',
}

/**
 * Compile-time-checked names for MJML templates registered in
 * `email-templates.ts`. Producers (workers, services) MUST reference
 * an enum member when emitting `NOTIFICATION_EMAIL_REQUESTED` so a typo
 * in `templateName` becomes a build error rather than a runtime fallback.
 *
 * Each value matches the basename of an `.mjml` file under
 * `packages/core/utils/src/email/templates/`.
 */
export enum EmailTemplateName {
	TICKET_CREATED_NOTIFICATION = 'ticket-created-notification',
	PENDING_APPROVALS_DIGEST = 'pending-approvals-digest',
}
